/* eslint-disable react/no-array-index-key */
/* eslint-disable react/no-this-in-sfc */
import { useQuery } from "@apollo/client";
import BoxedChartHeader from "components/charts/modern/abstract/BoxedChartHeader";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import useRootIssuer from "components/charts/modern/overlay/useRootIssuer";
import PercentageSeriesTooltip from "components/charts/modern/tooltip/PercentageSeriesTooltip";
import OnlyPremiumRender from "components/conversion/OnlyPremiumRender";
import aeonik from "components/utils/aeonik";
import type Highcharts from "highcharts/highstock";
import SayOnPayHistoryQuery from "lib/graphql/queries/SayOnPayHistoryQuery";
import type {
  ISayOnPayHistoryQuery,
  ISayOnPayHistoryQueryVariables,
} from "lib/graphql/queries/__generated__/SayOnPayHistoryQuery.generated";
import { uniqBy } from "lodash";
import { renderToStaticMarkup } from "react-dom/server";
import { FormattedMessage } from "react-intl";
import colors from "tailwindcss/colors";
import { ISecurityCountry } from "types/graphqlTypes";

export default function SayOnPayChart() {
  const issuer = useRootIssuer();
  const tickerSymbol = issuer.__typename === "AmericanCompanyIssuer" ? issuer.primaryTickerSymbol : "";
  const { data } = useQuery<ISayOnPayHistoryQuery, ISayOnPayHistoryQueryVariables>(SayOnPayHistoryQuery, {
    skip: !tickerSymbol,
    variables: {
      country: ISecurityCountry.Us,
      tickerSymbol: issuer.__typename === "AmericanCompanyIssuer" ? issuer.primaryTickerSymbol : "",
    },
  });
  if (!tickerSymbol) {
    return null;
  }
  if (data?.issuerByTickerSymbol?.__typename !== "AmericanCompanyIssuer") {
    return null;
  }
  if (!data || data.issuerByTickerSymbol.sayOnPayHistory.length === 0) {
    return null;
  }

  const sayOnPayHistory = uniqBy(
    [...(data?.issuerByTickerSymbol.sayOnPayHistory || [])].reverse(),
    (node) => node.fiscalYear,
  );
  const sayOnPayFor = sayOnPayHistory.map((node) => ({
    x: node.fiscalYear,
    y: node.sayOnPayFor || 0,
  }));
  const sayOnPayAgainst = sayOnPayHistory.map((node) => ({
    x: node.fiscalYear,
    y: node.sayOnPayAgainst || 0,
  }));
  const sayOnPayAbstention = sayOnPayHistory.map((node) => ({
    x: node.fiscalYear,
    y: node.sayOnPayAbstention || 0,
  }));
  const sayOnPayBrokerNonVotes = sayOnPayHistory.map((node) => ({
    x: node.fiscalYear,
    y: node.sayOnPayBrokerNonVotes || 0,
  }));
  const atFiscalYears = sayOnPayHistory.map((node) => {
    return String(node.fiscalYear);
  });

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      plotBorderWidth: 1,
      plotBorderColor: colors.slate[100],
      height: null,
    },
    scrollbar: {
      enabled: false,
    },
    legend: {
      align: "left",
      enabled: true,
      padding: 0,
      alignColumns: true,
      itemDistance: 16,
    },
    xAxis: {
      categories: atFiscalYears,
      gridLineWidth: 1,
      gridLineColor: colors.slate[100],
      lineWidth: 0,
      lineColor: colors.slate[100],
      tickWidth: 0,
      labels: {
        format: "{value}",
        enabled: true,
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      crosshair: {
        color: colors.slate[300],
        width: 1,
      },
    },
    yAxis: {
      lineWidth: 0,
      lineColor: colors.slate[100],
      gridLineWidth: 0,
      gridLineColor: colors.slate[100],
      tickWidth: 0,
      labels: {
        enabled: true,
        align: "center",
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
        formatter() {
          const valueFormatter = new Intl.NumberFormat("en", {
            notation: "compact",
            minimumFractionDigits: 0,
            maximumFractionDigits: 4,
          });
          return valueFormatter.format(parseFloat(String(this.value)));
        },
      },
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    series: [
      {
        type: "column",
        data: sayOnPayFor,
        name: "For",
        color: colors.lime[100],
        borderColor: colors.lime[600],
        borderWidth: 1.5,
      },
      {
        type: "column",
        data: sayOnPayBrokerNonVotes,
        name: "Broker non-votes",
        color: colors.indigo[100],
        borderColor: colors.indigo[600],
        borderWidth: 1.5,
      },
      {
        type: "column",
        data: sayOnPayAgainst,
        name: "Against",
        color: colors.pink[100],
        borderColor: colors.pink[600],
        borderWidth: 1.5,
      },
      {
        type: "column",
        data: sayOnPayAbstention,
        name: "Abstention",
        color: colors.slate[100],
        borderColor: colors.slate[600],
        borderWidth: 1.5,
      },
    ],
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      borderColor: colors.slate[300],
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      hideDelay: 0,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        return renderToStaticMarkup(<PercentageSeriesTooltip context={this} />);
      },
    },
    plotOptions: {
      series: {
        stacking: "normal",
        states: {
          hover: {
            lineWidth: 2,
          },
        },
      },
      column: {
        borderRadius: {
          where: "end",
          radius: 12,
          scope: "stack",
        },
      },
    },
  };

  return (
    <div className="relative space-y-4 rounded-xl border bg-white p-6 ring-4 ring-slate-100 ring-offset-1 ring-offset-slate-100">
      <BoxedChartHeader
        tickerSymbol={tickerSymbol}
        paywallDisplayOrigin="Say on pay chart"
        title={<FormattedMessage defaultMessage="Voto na Remuneração" id="1lUEHk" />}
        subtitle={
          <FormattedMessage
            defaultMessage="A quantidade e a porcentagem dos votos sobre a remuneração dos executivos."
            id="wU/hCh"
          />
        }
      />

      <OnlyPremiumRender>
        <>
          <HighchartsChart className="h-64 w-80 sm:w-full" options={options} />

          <p className="rounded-md bg-slate-50 p-2 px-3 text-sm italic leading-5 tracking-wide text-slate-700">
            <strong className="font-bold">{"Broker non-votes: "}</strong>
            <FormattedMessage defaultMessage="% de acionistas que não participaram da votação." id="p+G3Fh" />
          </p>
        </>
      </OnlyPremiumRender>
    </div>
  );
}
